/**
 * Invite activation landing — variant 3 (warm showroom).
 * The link `/invite/:token` is single-use and expires in 7 days. On success
 * the backend creates the password AND opens a session cookie so the user
 * lands already logged in.
 */
import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { KeyRound, Loader2, ArrowRight, ShieldCheck, AlertTriangle } from "lucide-react";

import { api } from "@/api/client";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { useGate } from "@/context/GateContext";
import { Input } from "@/components/ui/input";
import { CanalesLogo } from "@/components/CanalesLogo";

// Live URL that streams the AI showroom PNG stored in db.illustrations.
const BG_URL = (base) => `${base}/api/illustrations/welcome_invite_bg`;

export default function InviteActivate() {
  const { token } = useParams();
  const { lang } = useLang();
  const { refresh } = useAuth() || {};
  const gate = useGate() || {};
  const nav = useNavigate();

  const [state, setState] = useState({ loading: true, valid: null, info: null, reason: "" });
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await api.get(`/public/invite/${token}`);
        if (cancel) return;
        setState({ loading: false, valid: !!data.valid, info: data, reason: data.reason || "" });
      } catch (e) {
        if (!cancel) {
          setState({ loading: false, valid: false, info: null, reason: "network" });
        }
      }
    })();
    return () => { cancel = true; };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) {
      toast.error(lang === "es" ? "Mínimo 6 caracteres" : "Min 6 characters");
      return;
    }
    if (pw !== confirm) {
      toast.error(lang === "es" ? "Las contraseñas no coinciden" : "Passwords don't match");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/public/invite/${token}/activate`, { new_password: pw });
      localStorage.setItem("has_session", "1");
      if (refresh) await refresh();
      if (gate?.check) await gate.check();
      toast.success(lang === "es" ? `¡Bienvenido, ${data.name}!` : `Welcome, ${data.name}!`);
      if (data.role === "manager") nav("/onboarding", { replace: true });
      else if (data.role === "seller") nav("/magazine", { replace: true });
      else nav("/admin", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally {
      setBusy(false);
    }
  };

  // === Loading / invalid states share the same photo backdrop ===
  const wrapper = (children) => (
    <div
      data-testid="invite-page"
      className="min-h-screen w-full relative flex items-center justify-center px-4 py-10 overflow-hidden"
      style={{
        backgroundImage: `linear-gradient(135deg, rgba(13,26,63,0.35), rgba(255,246,217,0.25)), url(${BG_URL(backendUrl)})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <div className="absolute inset-0 bg-white/10 backdrop-blur-[2px]" />
      <div className="relative w-full max-w-lg">{children}</div>
    </div>
  );

  if (state.loading) {
    return wrapper(
      <div className="brutal p-10 bg-white/95 text-center">
        <Loader2 className="w-8 h-8 mx-auto animate-spin text-canales-blue" />
        <p className="mt-3 font-body text-neutral-600">
          {lang === "es" ? "Verificando tu invitación…" : "Verifying your invite…"}
        </p>
      </div>
    );
  }

  if (!state.valid) {
    const map = {
      not_found: lang === "es" ? "Este link no existe." : "This link does not exist.",
      expired:   lang === "es" ? "Este link expiró. Pide uno nuevo a tu admin." : "This link has expired. Ask your admin for a new one.",
      already_used: lang === "es" ? "Este link ya se usó. Entra con tu email y contraseña en /login." : "This link was already used. Go to /login.",
      revoked:   lang === "es" ? "Tu admin revocó este link." : "This link was revoked by admin.",
      user_inactive: lang === "es" ? "Tu cuenta está desactivada. Contacta a tu admin." : "Your account is disabled.",
      network:   lang === "es" ? "Sin conexión. Vuelve a intentar." : "Network error. Try again.",
    };
    return wrapper(
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        className="brutal p-8 bg-white/95 text-center"
        data-testid="invite-invalid"
      >
        <AlertTriangle className="w-12 h-12 mx-auto text-canales-red" />
        <h1 className="mt-3 font-display font-black text-3xl">
          {lang === "es" ? "Link no válido" : "Invalid link"}
        </h1>
        <p className="mt-3 font-body text-neutral-700">
          {map[state.reason] || (lang === "es" ? "Algo salió mal." : "Something went wrong.")}
        </p>
        <Link
          to="/login"
          className="mt-6 inline-flex items-center gap-2 brutal-btn bg-canales-blue text-white px-6 py-3"
        >
          {lang === "es" ? "Ir al login" : "Go to login"} <ArrowRight className="w-4 h-4" />
        </Link>
      </motion.div>
    );
  }

  const info = state.info || {};
  return wrapper(
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="brutal p-8 sm:p-10 bg-white/95 backdrop-blur-md space-y-6 shadow-2xl"
    >
      <div className="text-center">
        <CanalesLogo width={190} className="mx-auto" />
        <h1
          className="mt-5 text-canales-blue"
          style={{
            fontFamily: "'Caveat', 'Kalam', 'Comic Sans MS', cursive",
            fontSize: "clamp(2.4rem, 5vw, 3.2rem)",
            fontWeight: 700,
            lineHeight: 1.05,
          }}
        >
          {lang === "es" ? "¡Bienvenido a la familia Canales!" : "Welcome to the Canales family!"}
        </h1>
        <p className="mt-2 font-body text-neutral-700 text-lg">
          {lang === "es" ? (
            <>Hola <span className="font-bold">{info.name}</span>, tu cuenta está lista.</>
          ) : (
            <>Hi <span className="font-bold">{info.name}</span>, your account is ready.</>
          )}
        </p>
        <p className="font-body text-neutral-600 mt-1">
          {lang === "es"
            ? "Crea tu contraseña para empezar tu ruta de aprendizaje."
            : "Create your password to start your learning path."}
        </p>
      </div>

      {/* Personal chip */}
      <div className="brutal-sm bg-canales-cream p-3 flex flex-wrap items-center gap-2 justify-center text-xs font-display font-black uppercase tracking-widest">
        <span className="px-2 py-0.5 rounded-full bg-white border border-black">
          {info.email}
        </span>
        {info.role && (
          <span className="px-2 py-0.5 rounded-full text-white"
                style={{ background: info.role === "manager" ? "#005BBB" : "#E3000F" }}>
            {info.role === "manager"
              ? (lang === "es" ? "Manager" : "Manager")
              : (lang === "es" ? "Vendedor" : "Seller")}
          </span>
        )}
        {info.location && (
          <span className="px-2 py-0.5 rounded-full bg-white border border-black">
            {info.location}
          </span>
        )}
      </div>

      {info.already_active ? (
        <div className="brutal-sm bg-canales-yellow p-3 text-center font-display font-bold text-sm">
          {lang === "es"
            ? "Ya activaste tu cuenta antes. Puedes crear una NUEVA contraseña aquí o entrar con la actual desde /login."
            : "Your account was already activated. You can set a new password here or sign in at /login."}
        </div>
      ) : null}

      <form onSubmit={submit} className="space-y-4">
        <label className="block">
          <span className="text-xs font-display font-black uppercase tracking-widest text-canales-red">
            {lang === "es" ? "Tu nueva contraseña" : "Your new password"}
          </span>
          <div className="mt-1 relative">
            <KeyRound className="absolute left-3 top-3.5 w-5 h-5 text-neutral-500" />
            <Input
              data-testid="invite-pw"
              type="password"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              autoComplete="new-password"
              placeholder={lang === "es" ? "Mínimo 6 caracteres" : "Min 6 chars"}
              autoFocus
              className="brutal-sm pl-10 py-6 text-lg"
            />
          </div>
        </label>

        <label className="block">
          <span className="text-xs font-display font-black uppercase tracking-widest text-canales-red">
            {lang === "es" ? "Confirmar contraseña" : "Confirm password"}
          </span>
          <div className="mt-1 relative">
            <KeyRound className="absolute left-3 top-3.5 w-5 h-5 text-neutral-500" />
            <Input
              data-testid="invite-confirm"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              placeholder="••••••••"
              className="brutal-sm pl-10 py-6 text-lg"
            />
          </div>
        </label>

        <button
          type="submit"
          disabled={busy || !pw || !confirm}
          data-testid="invite-activate"
          className="w-full brutal-btn bg-canales-red text-white px-6 py-4 text-lg flex items-center justify-center gap-2 font-display font-black disabled:opacity-60 rounded-full"
        >
          {busy ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <ShieldCheck className="w-5 h-5" />
          )}
          {lang === "es" ? "Activar mi cuenta" : "Activate my account"}
        </button>
      </form>

      <div className="text-center text-xs font-body text-neutral-600 border-t-2 border-dashed border-neutral-300 pt-4">
        {lang === "es"
          ? "Este link se puede usar una sola vez y expira en 7 días."
          : "This link is single-use and expires in 7 days."}
      </div>
    </motion.div>
  );
}
